import React from 'react';
import { css } from '@emotion/react';
import Textarea, { TextareaProps } from '../Basics/Textarea';
import tw from 'twin.macro';
import { CopyIcon } from '$/images/Icons';
import Button from '../../Button';

export const EmbedCodeBox: React.FC<TextareaProps & { onCopy?: () => void }> = ({
  value,
  className,
  onCopy,
  ...props
}) => {
  const [copied, setCopied] = React.useState<boolean>(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const timeout = React.useRef<number | undefined>();

  const copyToClipboard = (): void => {
    if (!textareaRef.current) return;
    const box = textareaRef.current;

    box.select();
    box.setSelectionRange(0, 99999);
    document.execCommand('copy');
    box.setSelectionRange(-1, -1);
    textareaRef.current?.blur();
    setCopied(true);
    onCopy && onCopy();
  };

  React.useEffect(() => {
    if (!copied) return;

    if (timeout.current) clearTimeout(timeout.current);
    timeout.current = setTimeout(() => setCopied(false), 3000) as unknown as number;

    return (): void => clearTimeout(timeout.current);
  }, [copied]);

  return (
    <div
      className={`${className}`}
      css={css`
        ${tw`flex items-stretch mb-1e w-full`}
      `}
    >
      <Button
        disabled={copied || props.disabled}
        onClick={copyToClipboard}
        className={`
          flex flex-col justify-center items-center
          text-gray-1 hover:text-color-input-field bg-white hover:bg-gray-100
          rounded-input border border-color-input-box px-1e border-r-0
        `}
        css={css`
          border-top-right-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
        `}
      >
        <div className='flex flex-col items-center text-xs '>
          <CopyIcon className='w-1_75e h-1_75e mb-1/4e' />
          <div className='min-w-3e'>{copied ? 'Copied' : 'Copy'}</div>
        </div>
      </Button>
      <Textarea
        className={`flex w-full transition-color duration-200 ${
          copied && 'bg-color-brand text-white'
        }`}
        css={css`
          ${tw`flex min-h-4e text-gray-800 text-sm`}
          padding: 0.5rem 1rem !important;
          font-family: Courier !important;
          border-top-left-radius: 0 !important;
          border-bottom-left-radius: 0 !important;
          resize: none;
        `}
        {...props}
        readOnly
        value={value}
        ref={textareaRef}
      />
    </div>
  );
};

export const PurchasePixel: React.FC<{
  className?: string;
  purchasePixelUrl?: string | null;
}> = ({ className, purchasePixelUrl }) => {
  if (!purchasePixelUrl) {
    return null;
  }

  const embedCode = `<img src="${purchasePixelUrl}" width="1" height="1" alt="" />`;
  return <EmbedCodeBox className={`mt-1/2e ${className}`} value={embedCode} />;
};
